import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, CreditCard, Loader2, Receipt, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th,
} from "@/components/common/Chrome";
import { Payouts } from "@/components/common/Payouts";
import { rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";

const STATUS_STYLE = {
  paid: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  failed: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  refunded: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
};

export default function Payments() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [sources, setSources] = useState(null);
  const [busy, setBusy] = useState("");
  const detail = useRowDetail();

  const load = useCallback(async () => {
    const [res, src] = await Promise.all([
      api.get("/payments/register"), api.get("/payments/sources"),
    ]);
    setData(res.data);
    setSources(src.data);
  }, []);

  useEffect(() => { load().catch(() => setData({ items: [], counters: {}, active: {} })); }, [load]);

  const postToLedger = async (row) => {
    setBusy(row.session_id);
    try {
      const res = await api.post(`/payments/register/${encodeURIComponent(row.session_id)}/post`);
      toast.success(`Posted ${res.data.donation_reference} · receipt ${res.data.receipt_no}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Loading label="Loading the payment register" />;
  const c = data.counters || {};
  const active = data.active || {};

  return (
    <div className="space-y-5" data-testid="payments-page">
      <PageHeader eyebrow="Charity Cloud" title="Online payments"
                  description={`Card and wallet gifts taken through the donor portal. ${
                    active.label || "No provider"} handles ${active.country || "—"} in ${
                    active.currency || "—"}.`} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
        <Kpi testId="kpi-payments-total" label="Transactions" value={c.transactions || 0}
             accent="sky" icon={CreditCard} />
        <Kpi testId="kpi-payments-captured" label="Captured" value={c.paid || 0}
             accent="emerald" icon={CheckCircle2} />
        <Kpi testId="kpi-payments-value" label="Captured value" accent="violet" icon={Wallet}
             value={`${active.currency || ""} ${(c.value || 0).toLocaleString()}`} />
        <Kpi testId="kpi-payments-pending" label="Awaiting payment" value={c.pending || 0}
             accent="amber" />
        <Kpi testId="kpi-payments-awaiting" label="Awaiting posting" value={c.awaiting_posting || 0}
             accent="amber" icon={ShieldCheck} />
      </div>

      <Tabs defaultValue="transactions">
        <TabsList>
          <TabsTrigger value="transactions" data-testid="tab-payment-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payment-payouts">Payouts</TabsTrigger>
          <TabsTrigger value="sources" data-testid="tab-payment-sources">Where it came from</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-4">
          <Panel testId="panel-payments-register" kicker="Register" title="Payment transactions">
        <Table testId="payments-table"
               head={<><Th>Reference</Th><Th>Donor</Th><Th>Provider</Th><Th>Amount</Th>
                 <Th>Payment</Th><Th>Gift</Th><Th>Receipt</Th><Th>Started</Th><Th /></>}>
          {data.items.length === 0 && <Empty colSpan={9} label="No online payments yet" />}
          {data.items.map((t) => (
            <tr key={t.session_id} className="row-hover cursor-pointer"
                data-testid={`payment-row-${t.session_ref}`}
                onClick={rowClick(() => detail.show(t, t.session_ref, t.session_ref,
                                                    `${t.provider_label} · ${t.currency}`))}>
              <Td className="font-mono text-[11px]">{t.session_ref}</Td>
              <Td>
                <div className="text-foreground">{t.donor_name}</div>
                <div className="font-mono text-[10.5px] text-muted-foreground">{t.fund_code}</div>
              </Td>
              <Td><Chip className="border-primary/40 bg-primary/10 text-primary">{t.provider_label || t.provider}</Chip></Td>
              <Td className="font-mono">{t.currency} {t.amount?.toLocaleString()}</Td>
              <Td>
                <Chip className={STATUS_STYLE[t.payment_status] || STATUS_STYLE.pending}>
                  {t.payment_status}
                </Chip>
              </Td>
              <Td className="text-[11px]">
                {t.donation_reference
                  ? <span className="font-mono">{t.donation_reference} · {t.donation_status}</span>
                  : <span className="text-muted-foreground">—</span>}
              </Td>
              <Td className="font-mono text-[10.5px]">{t.receipt_no || "—"}</Td>
              <Td className="font-mono text-[10.5px]">{dateTime(t.created_at)}</Td>
              <Td>
                {t.donation_status === "pending_approval" && can("payment", "approve") && (
                  <Button size="sm" variant="outline" disabled={busy === t.session_id}
                          onClick={() => postToLedger(t)}
                          data-testid={`payment-post-${t.session_ref}`}
                          className="h-7 rounded-full border-border px-2.5 text-[10px]">
                    {busy === t.session_id ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <><Receipt className="mr-1 h-3 w-3" /> Post & receipt</>}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>
        </TabsContent>

        <TabsContent value="payouts" className="mt-4">
          <Payouts />
        </TabsContent>

        <TabsContent value="sources" className="mt-4">
          <Panel testId="panel-payment-sources" kicker="Attribution"
                 title="Which link raised the money">
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <div>
                <div className="kicker mb-2">By channel</div>
                {(sources?.by_source || []).length === 0 && (
                  <p className="text-[11.5px] text-muted-foreground" data-testid="sources-empty">
                    Nothing captured yet.
                  </p>
                )}
                {(sources?.by_source || []).map((s) => (
                  <div key={s.source} data-testid={`source-row-${s.source}`}
                       className="flex items-center justify-between border-b border-border/60 py-1.5 text-[12px]">
                    <span className="text-foreground">{s.source}</span>
                    <span className="font-mono text-muted-foreground">
                      {active.currency} {s.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              <div>
                <div className="kicker mb-2">By campaign</div>
                {(sources?.by_campaign || []).length === 0 && (
                  <p className="text-[11.5px] text-muted-foreground">No campaign tags yet.</p>
                )}
                {(sources?.by_campaign || []).map((s) => (
                  <div key={s.campaign} data-testid={`campaign-row-${s.campaign}`}
                       className="flex items-center justify-between border-b border-border/60 py-1.5 text-[12px]">
                    <span className="text-foreground">{s.campaign}</span>
                    <span className="font-mono text-muted-foreground">
                      {active.currency} {s.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </TabsContent>
      </Tabs>
      {detail.sheet}
    </div>
  );
}
